/**
 * App-side SEO configuration (Story 30.2).
 *
 * Single source of truth for per-route <head> metadata, consumed by the
 * useSEO() hook. Keyed by public path.
 *
 * NOTE: this deliberately mirrors the BUILD-TIME route list in
 * scripts/publicRoutes.ts (the sitemap's PUBLIC_ROUTES). The two live in
 * separate TS projects (app vs tsconfig.node) so neither can import the
 * other (TS6305) — instead a lockstep test (seoConfig.lockstep.test.ts)
 * reads scripts/publicRoutes.ts via fs and asserts every public path here
 * has SEO config and vice versa. A new public page therefore cannot ship in
 * the sitemap without SEO metadata, or gain metadata without a sitemap entry.
 */

/** Canonical origin — must match SITE_ORIGIN in scripts/publicRoutes.ts (lockstep-tested). */
export const SITE_ORIGIN = "https://provenleap.com";

/** Default branded OG image (1200×630). Asset provisioning is Calin's if absent. */
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/assets/og/provenleap-og.png`;

export interface SEOConfig {
  /** <title> — unique, ≤60 chars, includes "ProvenLeap". */
  title: string;
  /** <meta name="description"> — 150–160 chars. */
  description: string;
  /** Path (leading "/") the canonical URL is built from. Defaults to the map key. */
  path?: string;
  /** robots directive. Public pages "index, follow" (default); app pages "noindex, nofollow". */
  robots?: string;
  /** og:type — "website" (default) or "article" etc. */
  ogType?: string;
  /** Absolute OG/Twitter image URL. Defaults to DEFAULT_OG_IMAGE. */
  image?: string;
  /** twitter:card — "summary_large_image" (default) or "summary". */
  twitterCard?: "summary" | "summary_large_image";
}

const DEFAULT_DESCRIPTION =
  "Connect 30 marketing platforms in minutes. ProvenLeap builds beautiful branded reports and delivers them on schedule. All integrations, on every plan.";

/**
 * Per-public-route SEO. Keys MUST equal the paths in
 * scripts/publicRoutes.ts PUBLIC_ROUTES (lockstep-tested).
 */
// Each entry sets `path` EXPLICITLY (equal to its key) so the canonical URL is
// deterministic — never derived from window.location.pathname, which would
// bleed a trailing slash or query string into the canonical (review fix) and
// would be unavailable during a no-window render.
export const SEO_CONFIG: Record<string, SEOConfig> = {
  "/": {
    title: "ProvenLeap - Automated Client Reporting for Marketing Agencies",
    description: DEFAULT_DESCRIPTION,
    path: "/",
    ogType: "website",
  },
  "/waitlist": {
    title: "Join the ProvenLeap Waitlist - Agency Reporting, Automated",
    description:
      "Automated, white-label client reporting for agencies. All 30 integrations on every plan, a premium easy-to-use experience, and an MCP connection to Claude & ChatGPT. Join the ProvenLeap waitlist.",
    path: "/waitlist",
    ogType: "website",
  },
  // Legal pages keep their pre-existing verbatim title/description (Story 30.2 AC5 —
  // migrate the hand-rolled useEffect to useSEO without changing the copy).
  "/privacy": {
    title: "Privacy Policy - ProvenLeap",
    description:
      "ProvenLeap Privacy Policy - Learn how we collect, use, and protect your data. GDPR-compliant privacy practices for marketing analytics.",
    path: "/privacy",
    ogType: "website",
  },
  "/terms": {
    title: "Terms of Service - ProvenLeap",
    description:
      "ProvenLeap Terms of Service - Understand your rights and responsibilities when using our marketing analytics platform.",
    path: "/terms",
    ogType: "website",
  },
  "/cookie-policy": {
    title: "Cookie Policy - ProvenLeap",
    description:
      "ProvenLeap cookie policy — the cookies and client-side storage we use and how to manage consent.",
    path: "/cookie-policy",
    ogType: "website",
  },
  "/blog": {
    title: "Blog - ProvenLeap",
    description:
      "Guides on marketing agency client reporting, automation, and AI-assisted analytics from the ProvenLeap team.",
    path: "/blog",
    ogType: "website",
  },
  "/about": {
    title: "About ProvenLeap - Founder & Mission",
    description:
      "Meet Calin Paraipan, founder of ProvenLeap: automated marketing analytics and white-label client reporting for agencies, with a premium UX and a Claude & ChatGPT (MCP) connection.",
    path: "/about",
    ogType: "website",
  },
  "/mcp": {
    title: "Claude AI Integration (MCP) - ProvenLeap",
    description:
      "ProvenLeap connects to Claude and ChatGPT via the Model Context Protocol (MCP), so agencies can ask AI about client performance and draft reports conversationally.",
    path: "/mcp",
    ogType: "website",
  },
  "/integrations": {
    title: "Integrations - ProvenLeap",
    description:
      "ProvenLeap connects to 30 marketing platforms — Google Ads, GA4, Meta, LinkedIn, Shopify, and more — all included on every plan. Browse every integration.",
    path: "/integrations",
    ogType: "website",
  },
  "/glossary": {
    title: "Marketing Analytics Glossary - ProvenLeap",
    description:
      "Definitions of marketing analytics and client-reporting terms: KPIs, ROAS, ACOS, attribution, Core Web Vitals, MCP, data blending, and more.",
    path: "/glossary",
    ogType: "website",
  },
};

/** robots value applied to all authenticated/auth (non-public) pages. */
export const NOINDEX_ROBOTS = "noindex, nofollow";
